import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const scrollPositions = new Map<string, number>();

/**
 * Remembers the scrollTop of a list container per URL (path + query) so
 * coming back from a detail page restores where you left off instead of
 * snapping to the top.
 *
 * Attach the returned ref to the page's outer list wrapper. The actual
 * scrolling element is often a nested box (e.g. ListTable's internal
 * overflow body, marked with `data-scroll-body`) rather than the wrapper
 * itself, so we look for that marker first and fall back to the wrapper.
 *
 * Saving and restoring are intentionally two separate effects:
 * - Saving is keyed only on `key` (the route), so it attaches once per
 *   real navigation and its cleanup only fires on a genuine route change —
 *   not every time `isReady` flips during a normal loading cycle. Tying
 *   saving to `isReady` as well caused that cleanup to stomp the just-saved
 *   position back to 0 the moment the next page started loading, wiping it
 *   out before restoration ever got a chance to read it.
 * - Restoring is keyed on `[key, isReady]` and retries across animation
 *   frames / DOM mutations until the container is actually tall enough to
 *   hold the saved position (rows can land a tick after `isReady` flips).
 */
export function useScrollRestoration<T extends HTMLElement>(isReady = true) {
  const ref = useRef<T>(null);
  const location = useLocation();
  const key = location.pathname + location.search;

  const getScrollEl = (): HTMLElement | null => {
    const container = ref.current;
    if (!container) return null;
    return (
      (container.querySelector('[data-scroll-body]') as HTMLElement | null) ||
      container
    );
  };

  useEffect(() => {
    const el = getScrollEl();
    if (!el) return;

    const handleScroll = () => {
      const current = getScrollEl();
      if (current) scrollPositions.set(key, current.scrollTop);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      const current = getScrollEl();
      if (current) scrollPositions.set(key, current.scrollTop);
      el.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!isReady) return;
    const saved = scrollPositions.get(key);
    if (!saved) return;

    let restored = false;
    const tryRestore = () => {
      if (restored) return;
      const el = getScrollEl();
      if (!el) return;
      if (el.scrollHeight - el.clientHeight >= saved - 4) {
        el.scrollTop = saved;
        restored = true;
      }
    };

    const rafIds: number[] = [];
    const scheduleRaf = (n: number) => {
      if (n <= 0 || restored) return;
      rafIds.push(
        requestAnimationFrame(() => {
          tryRestore();
          scheduleRaf(n - 1);
        }),
      );
    };
    scheduleRaf(20);

    const container = ref.current;
    const observer = container
      ? new MutationObserver(() => tryRestore())
      : null;
    if (container && observer) {
      observer.observe(container, { childList: true, subtree: true });
    }

    return () => {
      rafIds.forEach((id) => cancelAnimationFrame(id));
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, isReady]);

  return ref;
}
